import { type Editor, useEditor } from '@tiptap/react';
import { useCallback, useEffect } from 'react';
import { buildExtensions } from './extensions';

type Options = {
  value: string;
  onChange?: (markdown: string) => void;
  isEditable?: boolean;
  placeholder?: string;
  ariaLabel?: string;
};

export type UseRichTextEditor = {
  editor: Editor | null;
  focus: VoidFunction;
};

export const useRichTextEditor = ({
  value,
  onChange,
  isEditable = true,
  placeholder,
  ariaLabel,
}: Options): UseRichTextEditor => {
  // `editorProps.attributes` sets attributes on the actual ProseMirror
  // editable element. Passing `aria-label` via `<EditorContent aria-label=...>`
  // lands on a wrapper div instead, so screen readers (and Playwright's
  // `getByRole('textbox', { name })`) never see the label on the editable.
  // `role="textbox"` is explicit because the `[contenteditable=true]` →
  // textbox mapping is not consistently exposed in the accessibility tree.
  //
  // Default to `{}` (not `undefined`) when no aria-label: tiptap reads
  // `this.options.editorProps.dispatchTransaction` during `createView`, and
  // `editorProps: undefined` blows that lookup up at mount time.
  const editorProps = ariaLabel ? { attributes: { 'aria-label': ariaLabel, role: 'textbox' } } : {};

  const editor = useEditor({
    extensions: buildExtensions({ placeholder }),
    content: value,
    editable: isEditable,
    editorProps,
    onUpdate: ({ editor: e }) => {
      if (onChange) onChange(e.storage.markdown.getMarkdown());
    },
  });

  // When the form's `value` changes from outside (e.g. reset / hydration of an
  // existing note), push it into the editor. The guard avoids re-applying the
  // value we just emitted via onUpdate, which would otherwise re-run setContent
  // on every keystroke and move the caret to the end.
  useEffect(() => {
    if (!editor) return;

    const currentContent = editor.storage.markdown.getMarkdown();
    if (currentContent !== value) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [value, editor]);

  const focus = useCallback(() => {
    editor?.commands.focus();
  }, [editor]);

  return { editor, focus };
};
